<template>
    <div id="timetable">
        <h2 class="h1 text-center timetable-primary-color-color">
            <span v-if="!activitySelected.length || activitySelected.length > 1">
                {{ $t('timetable.name') }}
            </span>
            <span v-else>
                <i18next :translation="$t('timetable.timetable_for', { name: activitySelected[0].name })">
                    <template #name>
                        <strong>{{ activitySelected[0].name?.toLowerCase() }}</strong>
                    </template>
                </i18next>
            </span>
        </h2>

        <div class="row d-flex justify-content-between">
            <div class="col-12 col-lg-6 col-xl-5">
                <div class="form-group d-flex warehouse-select">
                    <label v-if="!warehouseMultiple || hideWarehouseFilter"
                           for="warehouses"
                           class="mb-0 me-2"
                           @click="toggleWarehouseSelect"><strong>{{ $t('timetable.rooster_of') }}:</strong>&nbsp;{{ warehousesSelected?.[0]?.name || '(onbekende locatie)' }}</label>
                    <CustomMultiSelect v-if="!hideWarehouseFilter"
                                       ref="warehouseSelect"
                                       v-model="warehousesSelected"
                                       :options="warehouses"
                                       :multiple="warehouseMultiple"
                                       class="w-100"
                                       name="warehouses"
                                       :max-visible-tags="2"
                                       :show-reset-button="isWarehouseSelectionNotDefault"
                                       :placeholder="$t(warehouseMultiple ? 'timetable.select_warehouses' : 'timetable.select_warehouse')"
                                       :search-placeholder="$t(warehouseMultiple ? 'timetable.search_warehouses' : 'timetable.search_warehouse')"
                                       track-by="id"
                                       label="name"
                                       @clear="warehousesSelected = getDefaultWarehouse()" />
                </div>
            </div>
            <div class="col-12 col-lg-6 col-xl-4 end">
                <CustomMultiSelect v-if="!hideActivityFilter"
                                   v-model="activitySelected"
                                   :options="activities"
                                   :multiple="activityMultiple"
                                   class="w-100"
                                   :max-visible-tags="2"
                                   :placeholder="$t(activityMultiple ? 'timetable.select_activities' : 'timetable.select_activity')"
                                   :search-placeholder="$t(activityMultiple ? 'timetable.search_activities' : 'timetable.search_activity')"
                                   track-by="id"
                                   label="name" />
            </div>
        </div>

        <!-- Mobile and tablet navigation -->
        <div class="row d-block d-xxl-none">
            <div class="col-12 d-block d-xl-none mt-3">
                <h4 class="text-center timetable-primary-color-color">
                    {{ weekTitle }}
                </h4>
            </div>
            <div class="col-12 d-flex justify-content-between align-items-center mt-3 mb-3">
                <div class="button-container">
                    <button class="btn btn-sm btn-outline-primary"
                            :disabled="isPrevWeekDisabled"
                            @click="prevWeek">
                        <i class="fas fa-chevron-left" />
                        <span class="d-none d-md-inline">
                            {{ $t('timetable.previous_week') }}
                        </span>
                        <span class="d-inline d-md-none">
                            {{ $t('timetable.previous') }}
                        </span>
                    </button>
                    <button class="btn btn-sm btn-outline-primary ms-2"
                            :disabled="isCurrentWeek"
                            @click="goToCurrentWeek">
                        <span>
                            {{ $t('timetable.this_week') }}
                        </span>
                    </button>
                </div>
                <h4 class="text-center timetable-primary-color-color mb-0 d-none d-xl-block">
                    {{ weekTitle }}
                </h4>
                <div class="button-container text-right">
                    <button class="btn btn-sm btn-outline-primary"
                            @click="nextWeek">
                        <span class="d-none d-md-inline">
                            {{ $t('timetable.next_week') }}
                        </span>
                        <span class="d-inline d-md-none">
                            {{ $t('timetable.next') }}
                        </span>
                        <i class="fas fa-chevron-right" />
                    </button>
                </div>
            </div>
        </div>

        <!-- Desktop navigation and mobile day-selector -->
        <div class="row">
            <div class="col-12 d-flex justify-content-between align-items-center mt-3 mb-3">
                <div class="button-container">
                    <button class="btn btn-outline-primary d-none d-xxl-inline"
                            :disabled="isPrevWeekDisabled"
                            @click="prevWeek">
                        <i class="fas fa-chevron-left" />
                        <span>
                            {{ $t('timetable.previous_week') }}
                        </span>
                    </button>
                    <button class="btn btn-outline-primary d-none d-xxl-inline ms-2"
                            :disabled="isCurrentWeek"
                            @click="goToCurrentWeek">
                        <span>
                            {{ $t('timetable.this_week') }}
                        </span>
                    </button>
                </div>
                <h4 class="text-center timetable-primary-color-color d-none d-xxl-block">
                    {{ weekTitle }}
                </h4>
                <div class="d-block d-xxl-none week-navigation flex-grow-1 mx-2">
                    <div class="week-days">
                        <button v-for="(day, index) in navigationDays"
                                :key="index"
                                class="day-button"
                                :class="{
                                    'active': isActiveDayButton(day.date),
                                    'clicked': clickedDay === format(day.date, 'yyyy-MM-dd'),
                                    'today': format(day.date, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd'),
                                    'disabled': format(day.date, 'yyyy-MM-dd') < format(today, 'yyyy-MM-dd')
                                }"
                                :disabled="format(day.date, 'yyyy-MM-dd')"
                                @click="navigateToDay(day.date)">
                            <div class="day-name">
                                {{ format(day.date, 'eeeeee', { locale: locales[i18next.language] || locales['en-GB'] }) }}
                            </div>
                            <div class="day-number">
                                {{ format(day.date, 'dd') }}
                            </div>
                        </button>
                    </div>
                </div>
                <div class="button-container text-right">
                    <button class="btn btn-outline-primary d-none d-xxl-inline"
                            @click="nextWeek">
                        <span>
                            {{ $t('timetable.next_week') }}
                        </span>
                        <i class="fas fa-chevron-right" />
                    </button>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-12">
                <div class="week-carousel"
                     :class="{ 'is-loading': isLoading }">
                    <div ref="weekCarousel"
                         class="owl-carousel">
                        <div v-for="(day, index) in daysToShow"
                             :key="index"
                             class="day-slide"
                             :class="{ 'is-today': format(day.date, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd') }">
                            <div class="day-header">
                                <div class="day-number">
                                    {{ day.date.getDate() }}
                                </div>
                                <div class="day-name">
                                    {{ day.name }}
                                </div>
                            </div>
                            <div class="day-content">
                                <template v-if="!isLoading && day.items.length">
                                    <transition-group name="list"
                                                      tag="ul"
                                                      class="moments-list">
                                        <li v-for="(moment, momentIndex) in getVisibleItems(day.items, day.date)"
                                            :key="`${format(day.date, 'dd-MM')}-${format(new Date(moment.start), 'HH:mm')}-${moment.extendedProps.ActivityID}-${moment.extendedProps.WarehouseID}-${moment.extendedProps.LocationID}-${momentIndex}-${(day.items || 0).length}`"
                                            :class="{
                                                'ending-soon': moment.ending,
                                                'has-child': moment.children?.length && moment.children.length === 1,
                                                'has-children': moment.children?.length && moment.children.length > 1,
                                                ' -exception-block': moment.extendedProps.AvailabilityException?.Type === 'CLOSURE'
                                            }">
                                            <!-- {{ moment.extendedProps.AvailabilityException }} -->
                                            <div class="card-content"
                                                 :class="{ 'clickable': moment.extendedProps.ActivityURL }"
                                                 @click="handleCardClick(moment)">
                                                <h6 class="timetable-primary-color">
                                                    <!-- <span v-if="moment.children?.length"
                                                          class="badge badge-pill"
                                                          :title="`${moment.children.length + 1} ${$t('timetable.blocks_available')}`"
                                                          @click="openReservationModal(moment)">
                                                        <font-awesome-icon :icon="icons.faLayerGroup" />&nbsp;{{ moment.children.length + 1 }}
                                                    </span> -->
                                                    <div v-if="moment.extendedProps.ActivityURL"
                                                         class="d-flex justify-content-between align-items-center w-100">
                                                        <a :href="moment.extendedProps.ActivityURL">
                                                            {{ moment.extendedProps.Name }}
                                                        </a>
                                                        <a v-if="moment.extendedProps.ActivityURL !== currentUrl"
                                                           :href="moment.extendedProps.ActivityURL"
                                                           class="btn btn-outline-info btn-sm btn-circle ml-2">
                                                            <!-- <i class="fas fa-info-circle"></i> -->
                                                            <FontAwesomeIcon :icon="icons.faInfo"
                                                                             fixed-width />
                                                        </a>
                                                    </div>
                                                    <span v-else-if="moment.extendedProps.AvailabilityException.Name">
                                                        {{ $t('Let op') }}: {{ moment.extendedProps.AvailabilityException.Name }}
                                                    </span>
                                                    <span v-else>
                                                        {{ moment.extendedProps.Name }}
                                                    </span>
                                                </h6>
                                                <div class="content-wrap">
                                                    <time>
                                                        {{ format(new Date(moment.start), 'eeeeee. HH:mm', { locale: locales[i18next.language] || locales['en-GB'], weekStartsOn: 1 }).replace(/^\w/, c => c.toUpperCase()) }} - {{ format(new Date(moment.end), 'HH:mm') }}
                                                    </time>
                                                    <p :class="{ 'mb-0': moment.extendedProps.occupation }">
                                                        {{ moment.extendedProps['Location.Name'] }}
                                                    </p>
                                                    <p v-if="moment.extendedProps.occupation && !moment.ending && !moment.children?.length">
                                                        <small>{{ $t('common.occupation') }}: {{ moment.extendedProps.occupation }}</small>
                                                    </p>
                                                </div>
                                                <div class="d-flex justify-content-end">
                                                    <template v-if="getMomentStatus(moment)">
                                                        <b-button class="btn btn-xs ml-1"
                                                                  :variant="getMomentStatus(moment).variant"
                                                                  :disabled="!getMomentStatus(moment).canReserve"
                                                                  :style="!getMomentStatus(moment).canReserve ? 'pointer-events: none;' : ''"
                                                                  @click.stop="openReservationModal(moment)">
                                                            {{ getMomentStatus(moment).text }}
                                                            <span v-if="getMomentStatus(moment).showQueueCount && moment.extendedProps.QueueQuantity > 0"
                                                                  class="badge badge-pill badge-light">
                                                                {{ moment.extendedProps.QueueQuantity }}
                                                            </span>
                                                        </b-button>
                                                    </template>
                                                    <template v-else-if="moment.children?.length && !moment.ending">
                                                        <b-button class="btn btn-xs ml-1"
                                                                  variant="outline-primary"
                                                                  @click.stop="openReservationModal(moment)">
                                                            {{ $t('timetable.blocks') }}
                                                        </b-button>
                                                    </template>
                                                </div>
                                            </div>
                                        </li>
                                    </transition-group>
                                    <div v-if="hasMoreItems(day.items)"
                                         class="show-more-wrapper"
                                         :class="{ 'expanded': expandedDays[format(day.date, 'yyyy-MM-dd')] }">
                                        <button class="btn btn-link btn-show-more"
                                                @click="toggleDayExpand(day.date)">
                                            <template v-if="!expandedDays[format(day.date, 'yyyy-MM-dd')]">
                                                <span>{{ $t('common.show_more', { count: day.items.length - maxPerDay }) }}</span>
                                                <i class="fas fa-chevron-down" />
                                            </template>
                                            <template v-else>
                                                <span>{{ $t('common.show_less') }}</span>
                                                <i class="fas fa-chevron-up" />
                                            </template>
                                        </button>
                                    </div>
                                </template>
                                <div v-else-if="!isLoading && !day.items.length"
                                     class="no-items-block">
                                    {{ $t('timetable.no_results') }}
                                </div>
                                <ul v-else
                                    class="moments-list">
                                    <li v-for="n in Math.floor(Math.random() * 5) + 1"
                                        :key="`loading-${n}`"
                                        class="b-skeleton b-skeleton-text b-skeleton-animate-wave"
                                        style="width: 100%; height: 5rem;" />
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div class="touch-drag-icon">
                        <div class="d-block d-xxl-none">
                            <svg xmlns="http://www.w3.org/2000/svg"
                                 width="45.999"
                                 height="28.325"
                                 viewBox="0 0 45.999 28.325">
                                <g id="Group_259"
                                   data-name="Group 259"
                                   transform="translate(-173 -2353.837)">
                                    <path id="hand-back-point-up-solid"
                                          d="M22.639,0a2.212,2.212,0,0,0-2.213,2.213V17.7a.885.885,0,0,1-1.77,0V14.622c-.111.077-.216.166-.321.249l-1.062.885A3.544,3.544,0,0,0,16,18.478v2.1a7.074,7.074,0,0,0,2.55,5.438l.3.249a8.845,8.845,0,0,0,5.665,2.052l6.534.006a7.081,7.081,0,0,0,7.081-7.081V15.933A2.656,2.656,0,0,0,33.7,13.958a2.658,2.658,0,0,0-4.426-1.77,2.658,2.658,0,0,0-4.42-1.776v-8.2A2.212,2.212,0,0,0,22.639,0Z"
                                          transform="translate(169 2353.837)" />
                                    <path id="Icon_fa-solid-arrow-right-long"
                                          data-name="Icon fa-solid-arrow-right-long"
                                          d="M17.67,13.167a1.127,1.127,0,0,0,0-1.593l-4.5-4.5a1.126,1.126,0,1,0-1.593,1.593l2.58,2.58H1.125a1.125,1.125,0,0,0,0,2.25H14.157l-2.58,2.58A1.126,1.126,0,0,0,13.17,17.67l4.5-4.5Z"
                                          transform="translate(201 2361.255)" />
                                    <path id="Icon_fa-solid-arrow-right-long-2"
                                          data-name="Icon fa-solid-arrow-right-long"
                                          d="M17.67,13.167a1.127,1.127,0,0,0,0-1.593l-4.5-4.5a1.126,1.126,0,1,0-1.593,1.593l2.58,2.58H1.125a1.125,1.125,0,0,0,0,2.25H14.157l-2.58,2.58A1.126,1.126,0,0,0,13.17,17.67l4.5-4.5Z"
                                          transform="translate(190.999 2386) rotate(180)" />
                                </g>
                            </svg>

                            <div class="subtext">
                                {{ $t('timetable.touch_drag_hint') }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <ReservationModal v-if="selectedItem"
                          ref="reservationModal"
                          :item="selectedItem"
                          :csrf-token="csrfToken"
                          :has-siblings="selectedStackedItem"
                          @show-siblings="showSiblings"
                          @close="handleModalClose"
                          @reserved="handleReservationComplete"
                          @show-added-to-cart="showAddedToCartModal = true" />

        <StackedMomentsModal v-if="selectedStackedItem"
                             ref="stackedMomentsModal"
                             :main-item="selectedStackedItem"
                             @close="selectedStackedItem = null"
                             @select-moment="handleStackedMomentSelect" />

        <add-to-cart-modal v-if="showAddedToCartModal"
                           @close="closeAddedToCartModal" />
    </div>
</template>

<script>
import { format, addDays, subDays, startOfDay } from 'date-fns'
import { isNull, debounce } from 'lodash'
import axios from 'axios'
import nl from 'date-fns/locale/nl'
import enGB from 'date-fns/locale/en-GB'
import i18next from 'i18next'
import 'owl.carousel'
import $ from 'jquery'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faLayerGroup, faInfo } from '@fortawesome/pro-solid-svg-icons'
import ReservationModal from './ReservationModal.vue'
import StackedMomentsModal from './StackedMomentsModal.vue'
import AddToCartModal from '../cart/AddToCartModal.vue'
import CustomMultiSelect from '../components/CustomMultiSelect.vue'
import reservationStatusMixin from './mixins/reservationStatusMixin'

export default {
  name: 'TimeTable',

  components: {
    FontAwesomeIcon,
    ReservationModal,
    StackedMomentsModal,
    AddToCartModal,
    CustomMultiSelect
  },

  mixins: [reservationStatusMixin],

  data () {
    return {
      isInitializing: true,
      csrfToken: this.$parent.csrfToken,
      warehouses: this.$parent.warehouses || [],
      warehousesSelected: [],
      warehouseMultiple: this.$parent.warehouseMultiple || false,
      hideWarehouseFilter: this.$parent.hideWarehouseFilter || false,
      activityMultiple: this.$parent.activityMultiple || false,
      hideActivityFilter: this.$parent.hideActivityFilter || false,
      currentUrl: this.$parent.currentUrl || null,
      maxPerDay: this.$parent.maxPerDay || null,
      expandedDays: {}, // Track which days are expanded
      locales: {
        'nl-NL': nl,
        'en-GB': enGB
      },
      currentWeekStart: new Date(),
      currentDate: new Date(),
      isLoading: false,
      carousel: null,
      visibleItems: 7,
      totalLoadedDays: null,
      items: {},
      loadedDateRanges: [],
      activities: [
        ...this.$parent.activities || []
      ],
      activitySelected: [],
      format,
      isNull,
      icons: {
        faLayerGroup,
        faInfo
      },
      currentModal: null,
      selectedItem: null,
      selectedStackedItem: null,
      showAddedToCartModal: false,
      i18next,
      lastSlideIndex: 0, // Track the last slide position
      currentResolution: this.getResolutionMode(window.innerWidth), // Track current resolution mode
      dragStartX: 0, // Track drag start position
      dragThreshold: 50, // Drag threshold in pixels
      isLoadingMore: false, // Track if we're loading more items
      navigationDays: [],
      clickedDay: null,
      contentHeight: 0 // Track the content height
    }
  },

  computed: {
    today () {
      return startOfDay(new Date())
    },

    weekTitle () {
      const endDate = addDays(this.currentWeekStart, this.totalLoadedDays - 1)
      const locale = this.locales[i18next.language] || this.locales['en-GB']
      const startDateStr = format(this.currentWeekStart, 'd MMMM', { locale })

      return `${startDateStr} - ${format(endDate, 'd MMMM', { locale })}`
    },

    isPrevWeekDisabled () {
      const prevWeek = subDays(this.currentWeekStart, this.visibleItems)
      return prevWeek < startOfDay(this.currentDate)
    },

    daysToShow () {
      // Show only the days we should see based on currentWeekStart
      return Array.from({ length: this.totalLoadedDays }, (_, index) => {
        const date = addDays(this.currentWeekStart, index)
        const formattedDate = format(date, 'yyyy-MM-dd')
        return {
          date,
          name: this.formatDayName(date),
          items: this.items[formattedDate] || []
        }
      })
    },

    activitySelectedName () {
      if (!this.activitySelected?.length) return this.$t('timetable.name')
      if (this.activitySelected.length === 1) {
        const activity = this.activities.find(activity => activity.id === this.activitySelected[0])
        return activity?.name
      }
      return this.$t('timetable.multiple_activities_selected', { count: this.activitySelected.length })
    },

    isWarehouseSelectionNotDefault () {
      return !this.warehousesSelected.every(selected =>
        this.getDefaultWarehouse().some(defaultWarehouse => defaultWarehouse.id === selected.id)
      )
    },

    isCurrentWeek () {
      const currentDate = new Date()
      const startOfCurrentWeek = startOfDay(currentDate)
      const endOfCurrentWeek = addDays(startOfCurrentWeek, 6)
      return this.currentWeekStart >= startOfCurrentWeek && this.currentWeekStart <= endOfCurrentWeek
    }
  },

  watch: {
    '$el.clientWidth': {
      handler (width) {
        const oldVisibleItems = this.visibleItems
        this.updateVisibleItems(width)

        if (oldVisibleItems !== this.visibleItems) {
          this.initCarousel()
        }
      }
    },

    currentWeekStart: {
      handler () {
        this.navigationDays = this.generateNavigationDays()
      }
    },

    contentHeight: {
      handler (newHeight) {
        if (newHeight > 0) {
          const carousel = this.$el.querySelector('.week-carousel')
          if (carousel) {
            carousel.style.minHeight = `${newHeight}px`
          }
        }
      },
      immediate: true
    },

    warehousesSelected: {
      handler (newVal, oldVal) {
        // Skip if this is the initial mount or during initialization
        if (typeof oldVal === 'undefined' || this.isInitializing) return

        this.isLoading = true
        this.items = {} // Clear existing items before fetching new data
        this.debouncedFetchData()

        if (this.warehousesSelected.length) {
          this.saveWarehouseSelection(this.warehousesSelected.map(warehouse => warehouse.id))
        } else {
          localStorage.removeItem('warehouseIds')
        }
      },
      deep: true
    },

    activitySelected: {
      handler (newVal, oldVal) {
        // Skip if this is the initial mount or during initialization
        if (typeof oldVal === 'undefined' || this.isInitializing) return

        this.isLoading = true
        this.items = {} // Clear existing items before fetching new data
        this.debouncedFetchData()
      },
      deep: true
    }
  },

  mounted () {
    if (!this.csrfToken) {
      console.error('CSRF token is not set! Required for reservation modal.') // eslint-disable-line no-console
    }

    this.currentWeekStart = startOfDay(this.currentWeekStart)
    this.currentDate = startOfDay(this.currentDate)
    this.updateVisibleItems(this.$el.clientWidth)
    this.initCarousel()

    // If we only have one warehouse, we need to set the multiple to false.
    this.warehouseMultiple = this.warehouses.length > 1
    this.hideWarehouseFilter = this.$parent.hideWarehouseFilter || this.warehouses.length <= 1
    if (this.$parent.hideWarehouseFilter && this.warehouses.length > 1) {
      console.error('🚨 TIMETABLE: hideWarehouseFilter is set to true, but multiple warehouses are given, this is unnecessary data loading!') // eslint-disable-line no-console
    }

    // Set initial warehouse and activity using $set to avoid triggering watchers
    this.$set(this, 'warehousesSelected', this.getDefaultWarehouse())

    if (this.$parent.activityId) {
      const activityFound = this.activities.find(activity => activity.id === this.$parent.activityId)
      if (activityFound) {
        this.$set(this, 'activitySelected', [activityFound])
      }
    }

    // Now fetch data once
    this.refreshCurrentView()

    // Reset initialization flag after initial setup
    this.$nextTick(() => {
      this.isInitializing = false
    })

    window.addEventListener('resize', this.handleResize)
  },

  beforeDestroy () {
    if (this.carousel) {
      this.carousel.trigger('destroy.owl.carousel')
    }
    window.removeEventListener('resize', this.handleResize)
  },

  methods: {
    getDefaultWarehouse () {
      let warehousesSelected = []

      if (this.$parent.warehouseId) {
        const warehouseFound = this.warehouses.find(warehouse => warehouse.id === this.$parent.warehouseId)
        if (warehouseFound) {
          warehousesSelected = [warehouseFound]
        }
      } else if (localStorage.getItem('warehouseIds')) {
        const warehousesFromStorage = localStorage.getItem('warehouseIds')
        warehousesSelected = this.warehouses.filter(warehouse => warehousesFromStorage.includes(warehouse.id))
      } else {
        warehousesSelected = this.warehouses.filter(warehouse => warehouse.FrontEndSequenceNumber === '0')
      }

      return warehousesSelected
    },

    getResolutionMode (width) {
      if (width >= 1400) return 'desktop'
      if (width >= 768) return 'tablet'
      return 'mobile'
    },

    updateVisibleItems (width) {
      // Set visible items based on screen size
      if (width >= 1400) {
        this.visibleItems = 7
      } else if (width >= 768) {
        this.visibleItems = 3
      } else {
        this.visibleItems = 1
      }

      // Always load 7 days regardless of screen size
      this.totalLoadedDays = 7
    },

    formatDayName (date) {
      const locale = this.locales[i18next.language] || this.locales['en-GB']
      return format(date, 'EEEE', { locale })
    },

    getCarouselConfig (startPosition = 0, onInitialized = null) {
      return {
        items: this.visibleItems,
        nav: false,
        dots: false,
        margin: 8,
        loop: false,
        mouseDrag: true,
        touchDrag: true,
        smartSpeed: 450,
        rewind: false,
        startPosition,
        rtl: false,
        slideBy: 1,
        onTranslated: this.handleCarouselTranslate,
        onInitialized: onInitialized || ((event) => {
          this.lastSlideIndex = event.item.index
        }),
        responsive: {
          0: {
            items: 1,
            mouseDrag: true,
            touchDrag: true,
            stagePadding: 20
          },
          768: {
            items: 3,
            mouseDrag: true,
            touchDrag: true,
            stagePadding: 20
          },
          1400: {
            items: 7,
            mouseDrag: false,
            touchDrag: false,
            stagePadding: 0
          }
        },
        onResized: () => {
          this.handleResize()
        }
      }
    },

    initCarousel () {
      if (this.carousel) {
        this.carousel.trigger('destroy.owl.carousel')
      }

      this.$nextTick(() => {
        this.carousel = $(this.$refs.weekCarousel).owlCarousel(this.getCarouselConfig())

        // Add event listeners using the correct event names
        this.carousel.on('drag.owl.carousel', this.handleCarouselDrag)

        // Add a small adjustment to the stage width after initialization
        this.adjustStageWidth()
      })
    },

    // Fixes weird owl carousel bug where the stage width is too strict and kicks off the last day.
    adjustStageWidth () {
      this.$nextTick(() => {
        const owlStage = this.$refs.weekCarousel.querySelector('.owl-stage')
        if (owlStage) {
          const currentWidth = owlStage.offsetWidth
          owlStage.style.width = `${currentWidth + 1}px`
        }
      })
    },

    handleCarouselDrag (event) {
      if (this.currentResolution === 'desktop') return

      if (!this.dragStartX) {
        // Store the initial drag position
        this.dragStartX = event.relatedTarget._drag.stage.start.x
      }
    },

    handleCarouselTranslate (event) {
      const currentIndex = event.item.index
      if (currentIndex === null || this.lastSlideIndex === null) {
        this.lastSlideIndex = currentIndex
        return
      }

      // Get all currently visible days
      const visibleDays = Array.from({ length: this.visibleItems }, (_, i) => {
        const day = this.daysToShow[currentIndex + i]
        return day ? day.date : null
      }).filter(Boolean)

      if (visibleDays.length > 0) {
        // Update navigation days to show next 7 days from the current week start
        this.navigationDays = Array.from({ length: 7 }, (_, i) => {
          const dayDate = addDays(this.currentWeekStart, i)
          return {
            date: new Date(dayDate),
            isActive: visibleDays.some(visibleDay =>
              format(visibleDay, 'yyyy-MM-dd') === format(dayDate, 'yyyy-MM-dd')
            )
          }
        })
      }

      this.lastSlideIndex = currentIndex
    },

    async changeWeek (isNext = true) {
      if (this.isLoading) return

      // Get the current active day index
      const currentIndex = this.carousel?.find('.owl-item.active').first().index() || 0
      const currentActiveDay = this.daysToShow[currentIndex]?.date
      const currentDayOfWeek = currentActiveDay ? currentActiveDay.getDay() : 0

      const offset = 7 // Always move by 7 days
      const targetDate = isNext
        ? addDays(this.currentWeekStart, offset)
        : subDays(this.currentWeekStart, offset)

      if (!isNext && targetDate < startOfDay(this.currentDate)) return

      this.isLoading = true

      try {
        // Check if we have cached data for this week and activity combination
        const cachedRange = this.loadedDateRanges.find(range =>
          format(range.start, 'yyyy-MM-dd') === format(targetDate, 'yyyy-MM-dd') &&
          format(range.end, 'yyyy-MM-dd') === format(addDays(targetDate, this.totalLoadedDays - 1), 'yyyy-MM-dd') &&
          range.activitySelected === this.activitySelected.join(',')
        )

        if (cachedRange) {
          // Use cached data
          this.processEvents(cachedRange.items)
        } else {
          // Load new data if not cached
          await this.fetchTimeTableData(targetDate, addDays(targetDate, this.totalLoadedDays - 1))
        }

        // Update currentWeekStart which will trigger navigation days update
        this.currentWeekStart = targetDate

        // After data is loaded, find the same weekday in the new week
        this.$nextTick(() => {
          // Find the day with the same weekday in the new week
          const targetIndex = this.daysToShow.findIndex(day =>
            day.date.getDay() === currentDayOfWeek
          )

          if (targetIndex !== -1 && this.carousel) {
            // Destroy and reinitialize carousel
            const currentConfig = {
              ...this.getCarouselConfig(),
              startPosition: targetIndex,
              items: this.visibleItems
            }

            this.carousel.trigger('destroy.owl.carousel')
            this.$nextTick(() => {
              this.carousel = $(this.$refs.weekCarousel).owlCarousel(currentConfig)

              // Re-attach event listeners
              this.carousel.on('drag.owl.carousel', this.handleCarouselDrag)

              // Add the stage width adjustment after week change
              this.adjustStageWidth()
            })
          }
        })
      } catch (error) {
        console.error('🚨 TIMETABLE: Error changing week:', error) // eslint-disable-line no-console
      }
    },

    async fetchTimeTableData (startDate, endDate) {
      const params = {
        function: 'dataduiker/fcSubscriptionAgenda',
        StartDate: format(startDate, 'yyyy-MM-dd'),
        EndDate: format(addDays(endDate, 1), 'yyyy-MM-dd')
      }

      // Add warehouse filter if warehouses are selected
      if (this.warehousesSelected?.length) {
        params.WarehouseID = this.warehousesSelected.map(warehouse => warehouse.id).join(',')
      } else {
        params.WarehouseID = this.warehouses.map(warehouse => warehouse.id).join(',') // All warehouses.
      }

      // Add activity filter if activities are selected
      if (this.activitySelected?.length) {
        params.ActivityID = this.activitySelected.map(activity => activity.id).join(',')
      } else {
        params.ActivityID = this.activities.map(activity => activity.id).join(',') // All activities.
      }

      return axios.get('/php/xhr/ajax.php', { params })
        .then(response => {
          // Store the exact date range that was fetched
          if (!this.loadedDateRanges.find(range =>
            format(range.start, 'yyyy-MM-dd') === format(startDate, 'yyyy-MM-dd') &&
            format(range.end, 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd') &&
            range.activitySelected === this.activitySelected.join(',')
          )) {
            this.loadedDateRanges.push({
              start: startDate,
              end: endDate,
              items: response.data.items,
              activitySelected: this.activitySelected.join(',')
            })
          }

          // Only process new items without clearing existing ones
          if (response.data.items.length) {
            this.processEvents(response.data.items)
          } else {
            this.items = {}
            this.isLoading = false
          }

          return response.data
        })
    },

    processEvents (events, clearExisting = true) {
      // Don't clear existing items when using cached data
      const groupedItems = clearExisting ? {} : { ...this.items }
      const dayTracking = {}

      if (!events.length) {
        if (clearExisting) {
          this.items = {}
        }
        return
      }

      const now = new Date()
      const fifteenMinutesAgo = new Date(now.getTime() - (15 * 60000))

      events.forEach(event => {
        const day = event.start.split('T')[0]
        const itemEnd = new Date(event.end)

        // Skip if the event has ended more than 15 minutes ago
        if (itemEnd < fifteenMinutesAgo) {
          return
        }

        // Initialize both groupedItems and dayTracking for this day if they don't exist
        if (!groupedItems[day]) {
          groupedItems[day] = []
        }
        if (!dayTracking[day]) {
          dayTracking[day] = {}
        }

        // Add waiting line / Queue
        if (event.extendedProps.MaxEntryQuantity && event.extendedProps.QueueAttendanceYN) {
          event.queue = event.extendedProps.CurrentEntryQuantity === event.extendedProps.MaxEntryQuantity ? event.extendedProps.QueueQuantity : null
        }

        const eventKey = `${event.extendedProps.ActivityID}-${event.extendedProps.WarehouseID}`

        if (dayTracking[day][eventKey]) {
          // Add to existing stack
          if (!dayTracking[day][eventKey].children) {
            dayTracking[day][eventKey].children = []
          }
          dayTracking[day][eventKey].children.push(event)

          // Find the latest end time among all events (parent and children)
          const allEvents = [dayTracking[day][eventKey], ...dayTracking[day][eventKey].children]
          const latestEnd = allEvents.reduce((latest, evt) => {
            const endTime = new Date(evt.end)
            return endTime > latest ? endTime : latest
          }, new Date(dayTracking[day][eventKey].end))

          // Update the parent's end time if needed
          if (latestEnd > new Date(dayTracking[day][eventKey].end)) {
            dayTracking[day][eventKey].end = latestEnd.toISOString()
          }

          // Set ending flag if ended but still visible
          dayTracking[day][eventKey].ending = latestEnd <= now && latestEnd > fifteenMinutesAgo
        } else {
          // Create new stack
          dayTracking[day][eventKey] = { ...event }
          // Set ending flag if ended but still visible
          dayTracking[day][eventKey].ending = itemEnd <= now && itemEnd > fifteenMinutesAgo
          groupedItems[day].push(dayTracking[day][eventKey])
        }
      })

      // Sort items by start time, then by end time if start times are equal
      Object.keys(groupedItems).forEach(day => {
        groupedItems[day].sort((a, b) => {
          const aStart = new Date(a.start)
          const bStart = new Date(b.start)
          const aEnd = new Date(a.end)
          const bEnd = new Date(b.end)

          if (aStart - bStart !== 0) {
            return aStart - bStart
          }
          return aEnd - bEnd // Secondary sorting by end time
        })
      })

      // Merge with existing items instead of replacing
      this.items = { ...this.items, ...groupedItems }

      // Update content height after items are loaded
      this.$nextTick(() => {
        this.$el.querySelector('.week-carousel').style.minHeight = null // remove minHeight first to get the new calculated height.

        setTimeout(() => {
          this.isLoading = false
          this.updateContentHeight()
        }, 250)
      })
    },

    updateContentHeight () {
      setTimeout(() => {
        const carousel = this.$el.querySelector('.week-carousel')
        this.contentHeight = carousel.offsetHeight
      }, 500)
    },

    async refreshCurrentView () {
      this.isLoading = true
      this.items = {} // Clear existing items

      // Always load 7 days using totalLoadedDays
      await this.fetchTimeTableData(this.currentWeekStart, addDays(this.currentWeekStart, this.totalLoadedDays - 1))
    },

    async nextWeek () {
      return this.changeWeek(true)
    },

    async prevWeek () {
      return this.changeWeek(false)
    },

    toggleDayExpand (date) {
      const formattedDate = format(date, 'yyyy-MM-dd')
      this.$set(this.expandedDays, formattedDate, !this.expandedDays[formattedDate])
    },

    getVisibleItems (items, date) {
      if (!this.maxPerDay) return items

      const formattedDate = format(date, 'yyyy-MM-dd')
      if (this.expandedDays[formattedDate]) {
        return items
      }

      return items.length >= this.maxPerDay ? items.slice(0, this.maxPerDay) : items
    },

    hasMoreItems (items) {
      return this.maxPerDay && items.length > this.maxPerDay
    },

    openReservationModal (moment) {
      if (moment.children && moment.children.length) {
        this.selectedStackedItem = moment
        return
      }

      const status = this.getMomentStatus(moment)
      if (!status || !status.canReserve) {
        return
      }
      this.selectedItem = moment
      this.$nextTick(() => {
        this.$refs.reservationModal.fetchReservationOptions()
      })
    },

    showSiblings () {
      this.selectedItem = null
      this.openReservationModal(this.selectedStackedItem)
    },

    handleModalClose () {
      this.selectedItem = null
      this.selectedStackedItem = null
    },

    handleReservationComplete () {
      this.showAddedToCartModal = true
      this.refreshCurrentView()
    },

    handleStackedMomentSelect (payload) {
      this.selectedStackedItem = payload.stacked ? payload.stacked : null
      this.selectedItem = payload.moment
      this.$nextTick(() => {
        this.$refs.reservationModal.fetchReservationOptions()
      })
    },

    handleResize: debounce(function () {
      const width = window.innerWidth
      const oldResolution = this.currentResolution
      const newResolution = this.getResolutionMode(width)

      // Update resolution mode if changed
      if (oldResolution !== newResolution) {
        this.currentResolution = newResolution
        this.updateVisibleItems(width)
      }

      // Reinitialize carousel with new config if resolution changed
      if (oldResolution !== newResolution && this.carousel) {
        const currentIndex = this.carousel.find('.owl-item.active').first().index()
        this.carousel.trigger('destroy.owl.carousel')
        this.$nextTick(() => {
          this.carousel = $(this.$refs.weekCarousel).owlCarousel(this.getCarouselConfig(currentIndex))
          this.carousel.on('drag.owl.carousel', this.handleCarouselDrag)
        })
      }

      this.adjustStageWidth()
    }, 250),

    closeAddedToCartModal () {
      this.showAddedToCartModal = false
      this.handleReservationComplete()
    },

    debouncedFetchData: debounce(function () {
      this.fetchTimeTableData(
        this.currentWeekStart,
        addDays(this.currentWeekStart, this.totalLoadedDays - 1)
      )
    }, 300),

    generateNavigationDays () {
      // Always show next 7 days from current week start
      return Array.from({ length: 7 }, (_, i) => {
        const currentDate = addDays(this.currentWeekStart, i)
        return {
          date: new Date(currentDate),
          isActive: i < this.visibleItems // Active if it's within the visible range
        }
      })
    },

    isActiveDayButton (date) {
      if (!this.carousel) return false

      const currentIndex = this.carousel.find('.owl-item.active').first().index()
      const formattedTargetDate = format(date, 'yyyy-MM-dd')

      // Get the dates of currently visible slides
      const visibleDates = Array.from({ length: this.visibleItems }, (_, i) => {
        const day = this.daysToShow[currentIndex + i]
        return day ? format(day.date, 'yyyy-MM-dd') : null
      }).filter(Boolean)

      return visibleDates.includes(formattedTargetDate)
    },

    async navigateToDay (date) {
      const formattedDate = format(date, 'yyyy-MM-dd')

      // Find the index of the day in the carousel
      const dayIndex = this.daysToShow.findIndex(day =>
        format(day.date, 'yyyy-MM-dd') === formattedDate
      )

      if (dayIndex !== -1 && this.carousel) {
        // Set clicked state
        this.clickedDay = formattedDate

        // Clear clicked state after animation completes
        setTimeout(() => {
          this.clickedDay = null
        }, 350) // 300ms for animation + 50ms buffer

        // Navigate to the day
        this.carousel.trigger('to.owl.carousel', [dayIndex, 300])
      }
    },

    shouldShowReservationButton (moment) {
      return this.canReserve(moment)
    },

    toggleWarehouseSelect () {
      // Find the CustomMultiSelect component and toggle it
      const warehouseSelect = this.$refs.warehouseSelect

      if (warehouseSelect) {
        this.$refs.warehouseSelect.toggleDropdown()
      }
    },

    goToCurrentWeek () {
      this.isLoading = true
      const today = startOfDay(new Date())
      const startOfWeek = startOfDay(today)

      // Check if we already have this week loaded
      const cachedRange = this.loadedDateRanges.find(range =>
        format(range.start, 'yyyy-MM-dd') === format(startOfWeek, 'yyyy-MM-dd')
      )

      // We have the data, just update the view
      this.currentWeekStart = startOfWeek
      this.$nextTick(() => {
        this.processEvents(cachedRange.items)
      })
    },

    saveWarehouseSelection (warehouseIds) {
      localStorage.setItem('warehouseIds', warehouseIds)
    },

    handleCardClick (moment) {
      // Only navigate to ActivityURL if it exists
      if (moment.extendedProps.ActivityURL) {
        window.location.href = moment.extendedProps.ActivityURL
      }
    }
  }
}
</script>
